/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  Fragment,
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import { CollapseProps } from 'antd/lib/collapse';
import {
  Collapse,
  Table,
  Button,
  message,
  Modal,
  Row,
  Popover,
  Tooltip,
} from 'antd';
import { useIntl } from 'react-intl';
import { ColumnsType } from 'antd/lib/table';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { queryCache, useMutation } from 'react-query';
import IconButton from '~components/_common/IconButton/IconButton';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import AppContext from '~src/contexts/AppContext';
import { useStyles } from '~src/hooks/useStyles';
import { SVFIconType } from '~components/atoms/Viewers/SVFViewer/components/SVFIconType';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import {
  DbAttachment,
  DbFile,
  DbFolder,
  DbModuleFile,
  DbShare,
  DbTenderFolder,
} from '~src/types/db';
import { ClassicRegistration } from '~src/services/api/modules';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import {
  downloadFileLastVersion,
  downloadFolder,
  downloadModuleFile,
  downloadMultiple,
  downloadMultipleModuleFiles,
  downloadMultipleModuleFilesWithFolders,
  downloadMultipleClassicRegistrationsWithFolders,
} from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { appLog } from '~src/utils/log';
import { useIsPath } from '~src/hooks/useIsPath';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { useIsSuperuser } from '~src/hooks/useIsSuperuser';
import { useRightSideViewState } from '~components/molecules/RightSideView/RightSideView.state';
import {
  dbTenderShareFileToTableEntry,
  isFolderAvailable,
  MovementEntry,
  TableEntry,
} from '~src/components/atoms/FileTables/FileTable.utils';
import { VersionSelect } from '~src/components/_common/VersionSelect/VersionSelect';
import { useGetItemPath } from '~src/hooks/useGetItemPath';
import { useProjectId } from '~src/hooks/useProjectId';
import { useModalActions } from '~src/contexts/ModalsContext';
import { styleFn } from './CherryPickPanel.style';
import {
  changeAttachmentsState,
  changeFilesState,
  changeFileState,
  createCommit,
  deleteCommit,
  loadFileVersions,
  requestFileLocation,
  updateCommit,
} from '~src/services/api';
import { formatDate } from '~src/utils/formatDate';
import { getLinkModuleFile } from '~src/services/api/modules';
import { useCherryPickFileDownload } from './useCherryPickFileDownload';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { DownloadingState, useStore } from '~src/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { updateShare } from '~src/services/api/shares';
import {
  getOfferStatusVisual,
  getShareStatusVisualIntl,
} from '~src/utils/shareUtils/useShareKeysStatusUtils';
import {
  manageCurrentCorrectionLetterMovements,
  updateMovementFlags,
} from '~src/services/api/movements';
import {
  changeSecondaryShareKeyState,
  patchSecondaryInviteMessages,
} from '~src/services/api/secondaryVendors';
import { useSharedTenderDetails } from '~src/hooks/api-hooks/useTenderDetails';
import { isTenderClosed } from '~src/utils/isTenderClosed';
import { SimpleDeleteModal } from '~components/popups-modals/DeleteModal/SimpleDeleteModal';
import { SimpleConfirmModal } from '~components/popups-modals/ConfirmModal/SimpleConfirmModal';

type MergedDbFile = DbFile & DbAttachment;

const CHERRY_PICK_MODES = [
  'documents',
  'shares',
  'movements',
  'archive',
  'search',
  'module',
  'deleted',
  'recents',
  'favourites',
  'my-tenders',
] as const;

const getModeKey = (path: string): string =>
  CHERRY_PICK_MODES.find((m) => path.includes(`/${m}`)) ?? 'other';

export interface CherryPickPanelProps {
  selectedItems: TableEntry[];
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  /** Collapse state change callback */
  onCollapse?: CollapseProps['onChange'];
  onRemoveItem?: (id: string) => void;
}

const CherryPickPanel: React.FC<CherryPickPanelProps> = ({
  selectedItems,
  setSelectedItems,
  onCollapse,
  onRemoveItem,
}) => {
  const intl = useIntl();
  const style = useStyles(styleFn);
  const location = useLocation();
  const paths = location.pathname.split('/');
  const { appContextState } = useContext(AppContext);
  const { fileVersionsContext } = useContext(FileVersionsContext);

  const getItemPath = useGetItemPath();
  const projectId = useProjectId();
  const currentFolder = useCurrentFolder();
  const isSuperuser = useIsSuperuser();
  const isOnArchive = useIsPath('/archive');
  const isOnModule = useIsPath('/module');
  const isOnSearch = useIsPath('/search');
  const isShares = useIsPath('/shares');
  const isMovements = useIsPath('/movements');
  // Primary-/Secondary Share Keys: vendor's My-Tenders -> Subcontractors
  // tab. No other route in the app contains '/subcontractors', and it
  // never overlaps isShares ('/shares').
  const isOnSubcontractors = useIsPath('/subcontractors');
  const isDocuments = useIsPath('/documents');
  const isOnDeleted = useIsPath('/deleted-files');
  const isOnRegistrations = useIsPath('/section-modules/sector');
  const { isOnTender, isOnMyTender } = useRightSideViewState();

  const history = useHistory();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    const prev = prevPathRef.current;
    const next = location.pathname;
    prevPathRef.current = next;

    if (getModeKey(prev) === getModeKey(next)) return;
    if (selectedItems.length === 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'cherry-pick-leave-title' }),
      content: intl.formatMessage({ id: 'cherry-pick-leave-content' }),
      okText: intl.formatMessage({ id: 'ok' }),
      cancelText: intl.formatMessage({ id: 'cancel' }),
      onOk: () => setSelectedItems([]),
      onCancel: () => {
        prevPathRef.current = prev; // prevent guard re-triggering on revert
        history.replace(prev);
      },
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const foldersQuery = useFolders(
    {
      as_admin:
        window.sessionStorage.getItem('appState') === 'superUser'
          ? true
          : undefined,
    },
    { enabled: false }
  );
  const shareModal = useModalActions('shareModal');
  const moveModal = useModalActions('moveModal');
  const deleteModal = useModalActions('deleteModal');
  const restoreToModal = useModalActions('restoreToModal');
  const copyModal = useModalActions('copyModal');
  const comparePdfModal = useModalActions('pdfCompare');
  const resendInvite = useModalActions('resendInvite');
  const correlationLetterModal = useModalActions('correlationLetterModal');
  const reactivateShareModal = useModalActions('reactivateShare');
  const inviteVendorsModal = useModalActions('inviteVendorsModal');

  // TODO: move to controller above
  const tenderId = paths[4];

  // Primary-/Secondary Share Keys (vendor tab): the acting Primary-Share-
  // Key's own identity, derived from /my-tenders/{shareId}-{shareKey}/
  // subcontractors - verified byte-for-byte equivalent to MyTendersTabs.tsx's
  // own shareId/shareKeyFromUrl derivation from shareIdKey. Renamed to avoid
  // colliding with admin's own tenderId/projectId vars above.
  const myTenderShareIdKey = paths[2];
  const { subShareId, subShareKey } = useMemo(() => {
    const raw = String(myTenderShareIdKey || '');
    const parts = raw.split('-');
    return {
      subShareId: parts[0] || '',
      subShareKey: parts.slice(1).join('-') || '',
    };
  }, [myTenderShareIdKey]);

  // Shares its react-query cache key with MyTendersTabs.tsx's identical
  // call, so this is not an extra real network request in practice.
  const subShareTenderDetails = useSharedTenderDetails(
    { id: subShareId, shareKey: subShareKey },
    { enabled: isOnSubcontractors && !!subShareId && !!subShareKey }
  );
  const subcontractorsTenderCompleted = isTenderClosed(
    subShareTenderDetails.data
  );

  const [subcontractorsBulkAction, setSubcontractorsBulkAction] = useState<{
    type: 'remove' | 'reactivate' | 'resend-invite';
    shares: DbShare[];
  } | null>(null);

  const [
    doBulkChangeSecondaryShareKeyState,
    { isLoading: isBulkChangingSecondaryShareKeyState },
  ] = useMutation(
    (vars: { shares: DbShare[]; action: 'remove' | 'reactivate' }) =>
      Promise.all(
        vars.shares.map((s) =>
          changeSecondaryShareKeyState({
            shareId: subShareId,
            shareKey: subShareKey,
            secondaryShareKey: s.share_key_id,
            action: vars.action,
          })
        )
      ),
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.secondaryShareKeys]);
        setSelectedItems([]);
        setSubcontractorsBulkAction(null);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'tender-share-update-failed' }));
      },
    }
  );

  const [
    doBulkResendSecondaryInvite,
    { isLoading: isBulkResendingSecondaryInvite },
  ] = useMutation(
    (vars: { shares: DbShare[] }) =>
      patchSecondaryInviteMessages({
        shareId: subShareId,
        shareKey: subShareKey,
        body: vars.shares.map((s) => ({
          share_key: s.share_key_id,
          email: s.share_key_email,
          title: s.share_key_title,
        })),
      }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries([QueryKeys.secondaryShareKeys]);
        setSelectedItems([]);
        setSubcontractorsBulkAction(null);
      },
      onError: () => {
        message.error(intl.formatMessage({ id: 'invite-share-edit-failed' }));
      },
    }
  );

  const hasRemovedSubcontractorShares =
    isOnSubcontractors &&
    selectedItems.some(
      (item) => item.dbShare?.share_key_state === DB_STATE.REMOVED
    );
  // Resend-Invite/Delete only make sense on shares that aren't removed yet;
  // if every selected share key is already removed, hide those buttons.
  const hasNonRemovedSubcontractorShares =
    isOnSubcontractors &&
    selectedItems.some(
      (item) => item.dbShare?.share_key_state !== DB_STATE.REMOVED
    );

  const [doBulkRestore, mutationBulkRestore] = useMutation(
    changeAttachmentsState,
    {
      onError: (error) => {
        appLog('BulkRestoreFiles => ', error);
        message.error(intl.formatMessage({ id: 'restore-failed' }));
        // commit &&
        //   deleteCommit({ projectId: String(projectId), commit }).catch(
        //     () => undefined
        //   );
        throw error;
      },
      onSuccess: async (data) => {
        // commit &&
        //   (await updateCommit({
        //     projectId: String(projectId),
        //     commitData: { ...commit, state: DB_STATE.PROCESSED },
        //   }));
        queryCache.invalidateQueries('files');
        queryCache.invalidateQueries(QueryKeys.tendersTenderAttachments);
        message.success(intl.formatMessage({ id: 'restore-success' }));
        setSelectedItems([]);
      },
    }
  );

  const [doManageMovementsToLetter] = useMutation(
    manageCurrentCorrectionLetterMovements,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['movements', projectId, tenderId]);
        message.success(
          intl.formatMessage({ id: 'correction-letter-movements-updated' })
        );
        setSelectedItems([]);
      },
    }
  );

  const [doUpdateMovementFlags] = useMutation(updateMovementFlags, {
    onSuccess: () => {
      queryCache.invalidateQueries(['movements', projectId, tenderId]);
      message.success(intl.formatMessage({ id: 'movement-flags-updated' }));
    },
  });

  const createMutationTrigger = (
    action: 'add_movement' | 'remove_movement',
    ids: number[]
  ) => {
    if (!projectId || !tenderId) return;
    doManageMovementsToLetter({
      projectId: Number(projectId),
      tenderId: Number(tenderId),
      action,
      body: { movement_ids: ids },
    });
  };

  const updateFlagsMutation = (
    id: number,
    action: 'set_ignore_on' | 'set_ignore_off'
  ) => {
    if (!projectId || !tenderId) return;
    doUpdateMovementFlags({
      project_id: Number(projectId),
      tender_id: Number(tenderId),
      movement_id: id,
      action,
    });
  };

  const hasRemovedShares =
    isShares &&
    selectedItems.some(
      (item) => item.dbShare?.share_key_state === DB_STATE.REMOVED
    );

  /** Table columns */
  const columns: ColumnsType<TableEntry> = [
    {
      key: 'key',
      dataIndex: 'key',
      width: 50,
      render: (key: TableEntry['key']) => (
        <IconButton
          icon={['far', 'times-circle']}
          color="red"
          customStyle={style.removeIcon}
          containerStyle={{ marginLeft: -6 }}
          click={() =>
            // remove item matching record's key
            setSelectedItems((prevItems) =>
              prevItems.filter((item) => item.key !== key)
            )
          }
          tooltip={intl.formatMessage({ id: 'remove-file' })}
        />
      ),
      shouldCellUpdate: (record, prevRecord) => record.key !== prevRecord.key,
    },
    {
      title: intl.formatMessage({ id: 'type' }),
      dataIndex: 'type',
      width: 50,
      align: 'center',
      onHeaderCell: () => ({ style: { padding: '4px 6px' } }),
      onCell: () => ({ style: { padding: '4px 6px' } }),
      render: (type, record) => {
        const extension = record.extension?.toLowerCase() || '';
        const isCadFile = ['rvt', 'dwg', 'ifc'].includes(extension);

        if (isCadFile) {
          return (
            <SVFIconType
              key={`file-icon-${record.id}`}
              version={fileVersionsContext[record.id]}
              extension={record.extension}
              variant="table"
              customTooltip={intl.formatMessage(
                { id: type.icon },
                { type: record.extension?.toUpperCase() }
              )}
              onClick={() => {
                // File icon click - no action
              }}
              type={type}
            />
          );
        }

        // Handle custom icons (module icons for classic registrations)
        if (type.customIcon) {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <NavIcon
                icon={type.customIcon}
                customStyle={{ height: '20px', width: 'auto' }}
              />
            </div>
          );
        }

        return (
          <IconButton
            iconStyle={type.iconStyle}
            icon={type.icon}
            customStyle={style.typeIcon}
            color={type.color}
            tooltip={
              type.icon
                ? intl.formatMessage(
                    { id: type.icon },
                    { type: record.extension?.toUpperCase() }
                  )
                : record.extension
                ? `${record.extension.toUpperCase()} file`
                : undefined
            }
            badge={type.is_empty && '0'}
            backgroundOff
          />
        );
      },
      shouldCellUpdate: (record, prevRecord) =>
        record.extension !== prevRecord.extension,
    },
    {
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: intl.formatMessage({ id: 'description' }),
      dataIndex: 'description',
      key: 'description',
      width: 210,
      ellipsis: true,
    },
    // @ts-ignore
    ...(!isDocuments
      ? [
          {
            title: intl.formatMessage({ id: 'ver' }),
            dataIndex: 'dbFile',
            key: 'ver',
            width: 80,
            align: 'center',
            onCell: () => ({ style: { padding: '4px 10px' } }),
            render: (file?: DbFile) => {
              if (!file) return null;
              return <VersionSelect file={file} />;
            },
            shouldCellUpdate: (record, prevRecord) =>
              record.dbFile?.versions !== prevRecord.dbFile?.versions,
          },
        ]
      : []),
    // @ts-ignore
    {
      title: intl.formatMessage({ id: 'path' }),
      dataIndex: 'parentId',
      key: 'path',
      render: (stringParentId: TableEntry['parentId']) => {
        const parentId =
          stringParentId === 'null' ? null : Number(stringParentId);

        const availableFolder =
          foldersQuery.data && isFolderAvailable(parentId, foldersQuery.data);

        if (isOnDeleted) {
          if (availableFolder) {
            return (
              <Link
                to={`/projects/${projectId}/archive/${
                  parentId !== null ? parentId : ''
                }`}>
                {getItemPath(parentId)}
              </Link>
            );
          } else {
            if (availableFolder === null) {
              return (
                <Fragment>
                  <IconButton
                    icon="ban"
                    customStyle={style.typeIcon}
                    color="red"
                    tooltip={intl.formatMessage({ id: 'modified-date' })}
                    backgroundOff
                  />
                  {getItemPath(parentId)}
                </Fragment>
              );
            } else {
              return (
                <Fragment>
                  <IconButton
                    icon={['fad', 'trash']}
                    customStyle={style.typeIcon}
                    color="red"
                    tooltip={intl.formatMessage({ id: 'folder-deleted' })}
                    backgroundOff
                  />
                  {getItemPath(parentId)}
                </Fragment>
              );
            }
          }
        } else {
          return (
            <Link
              to={`/projects/${projectId}/archive/${
                parentId !== null ? parentId : ''
              }`}>
              {getItemPath(parentId)}
            </Link>
          );
        }
      },
      shouldCellUpdate: (record, prevRecord) =>
        record.parentId !== prevRecord.parentId,
    },
    {
      title: intl.formatMessage({ id: 'modified-date' }),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      align: 'center' as const,
      render: (updatedAt: TableEntry['updatedAt'], record) => {
        let modified = updatedAt;

        // if file, show "modified" of selected version
        if (record.dbFile?.versions) {
          modified =
            fileVersionsContext[record.id]?.updated_at ||
            record.dbFile.versions[0]?.updated_at;
        }

        if (record.hasOwnProperty('dbModuleFile')) {
          modified = (record.dbModuleFile as DbModuleFile).created_at;
        }

        return formatDate(modified, 'short');
      },
    },
  ];

  // --- Helper Functions ---

  const shareRemoveItem = (entry: TableEntry) => {
    setSelectedItems?.((prevItems) =>
      prevItems.filter((item) => item.shareKey !== entry.shareKey)
    );
  };

  const shareColumns = [
    {
      key: 'share_key_id',
      dataIndex: 'share_key_id',
      width: 40,
      render: (_: any, record) => (
        <IconButton
          icon={['far', 'times-circle']}
          color="red"
          customStyle={style.removeIcon}
          containerStyle={{ marginLeft: -6 }}
          click={() => shareRemoveItem(record)}
          tooltip={intl.formatMessage({ id: 'remove-file' })}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'status' }),
      dataIndex: 'share_key_state',
      width: 50,
      align: 'center',
      render: (_: any, record: DbShare) => {
        const { icon, color, text } = getShareStatusVisualIntl(intl, record);
        return (
          <div css={style.alignCenterWrapper} style={{ color }}>
            <Tooltip title={text}>{icon}</Tooltip>
          </div>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'name' }),
      dataIndex: 'share_key_title',
      key: 'name',
    },
    {
      title: intl.formatMessage({ id: 'email' }),
      dataIndex: 'share_key_email',
      key: 'email',
    },
    {
      title: intl.formatMessage({ id: 'contact-name' }),
      dataIndex: 'share_key_contact_name',
      key: 'contact-name',
      render: (contactName: string) => contactName || '',
    },
    {
      title: intl.formatMessage({ id: 'shares-tab-table-opened-column' }),
      dataIndex: 'share_key_opened_at',
      key: 'opened',
      width: 110,
      render: (val: number) => (val ? formatDate(val, 'short') : '—'),
    },
    {
      title: intl.formatMessage({ id: 'accepted' }),
      dataIndex: 'share_key_claimed_at',
      key: 'accepted',
      width: 110,
      render: (val: number) => (val ? formatDate(val, 'short') : '—'),
    },
    {
      title: intl.formatMessage({ id: 'contracts' }),
      dataIndex: 'contracts',
      width: 110,
      ellipsis: true,
      render: (_: any, record: DbShare) => {
        const offers = record.offers;
        if (offers?.length === 0) {
          return null;
        } else if (offers?.length === 1) {
          const { color, text } = getOfferStatusVisual(intl, offers[0]);

          let icon = null;
          if (color === 'grey')
            icon = <FontAwesomeIcon icon={['far', 'stopwatch']} />;
          else if (color === 'green')
            icon = <FontAwesomeIcon icon={['fas', 'memo-circle-check']} />;
          else if (color === 'red')
            icon = <FontAwesomeIcon icon={['far', 'ban']} />;
          else if (color === 'orange')
            icon = <FontAwesomeIcon icon={['far', 'pen']} />;

          return (
            <Popover
              trigger="click"
              content={
                <div
                  css={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '15px',
                    padding: '10px',
                  }}>
                  {offers.map((offer, index) => {
                    const {
                      color: popColor,
                      text: popText,
                    } = getOfferStatusVisual(intl, offer);
                    return (
                      <div
                        key={index}
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                        }}>
                        <div
                          css={[
                            { height: '100%', width: '5px' },
                            style[popColor],
                          ]}
                        />
                        <div css={{ fontSize: '12px', lineHeight: '1.4' }}>
                          <div>
                            <b>Contract ID:</b> #{offer.contract_id}
                          </div>
                          <div>
                            <b>State: {popText}</b>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }>
              <div
                style={{
                  color,
                  border: `1px solid ${color}`,
                  borderRadius: '3px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '3px',
                }}>
                {icon}
                <strong>{text}</strong>
              </div>
            </Popover>
          );
        } else {
          // If more than 3 offers, show a summary with a popover.
          // If there are more than 3 offers, show first 3 + a "square" with hiddenCount
          if (offers?.length > 4) {
            const visibleOffers = offers?.slice(0, 3);
            const hiddenCount = offers.length - visibleOffers.length;
            return (
              <Popover
                trigger="click"
                content={
                  <div
                    css={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '15px',
                      padding: '10px',
                    }}>
                    {offers?.map((offer, index) => {
                      const { color, text } = getOfferStatusVisual(intl, offer);
                      return (
                        <div
                          key={index}
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}>
                          <div
                            css={[
                              { height: '100%', width: '5px' },
                              style[color],
                            ]}
                          />
                          <div css={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <div>
                              <b>Contract ID:</b> #{offer.contract_id}
                            </div>
                            {/*{offer.offer_id && (*/}
                            {/*  <div>*/}
                            {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                            {/*  </div>*/}
                            {/*)}*/}
                            {/*<div>*/}
                            {/*  <b>Offer created:</b>{' '}*/}
                            {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                            {/*</div>*/}
                            <div>
                              <b>State: {text}</b>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }>
                <div
                  css={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '4px', // control spacing between circles
                  }}>
                  {visibleOffers?.map((offer, index) => {
                    const { color, text } = getOfferStatusVisual(intl, offer);
                    return (
                      <Tooltip
                        key={index}
                        placement="top"
                        title={
                          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <div>
                              <b>Contract ID:</b> #{offer.contract_id}
                            </div>
                            {/*{offer.offer_id && (*/}
                            {/*  <div>*/}
                            {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                            {/*  </div>*/}
                            {/*)}*/}
                            {/*<div>*/}
                            {/*  <b>Offer created:</b>{' '}*/}
                            {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                            {/*</div>*/}
                            <div>
                              <b>State: {text}</b>
                            </div>
                          </div>
                        }>
                        <div
                          css={[style.tagPoint, style[color]]}
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    );
                  })}

                  {/* Square button */}
                  <div
                    css={{
                      width: '23px',
                      height: '23px',
                      backgroundColor: 'white',
                      border: '1px solid gray',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'black',
                      fontWeight: 'lighter',
                      fontSize: '13px',
                      borderRadius: '3px',
                      cursor: 'pointer',
                    }}
                    title="More contracts">
                    <strong>+{hiddenCount}</strong>
                  </div>
                </div>
              </Popover>
            );
          } else {
            // If there are exactly 2, 3, or 4 offers
            return (
              <Popover
                trigger="click"
                content={
                  <div
                    css={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '15px',
                      padding: '10px',
                    }}>
                    {offers?.map((offer, index) => {
                      const { color, text } = getOfferStatusVisual(intl, offer);
                      return (
                        <div
                          key={index}
                          css={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}>
                          <div
                            css={[
                              { height: '100%', width: '5px' },
                              style[color],
                            ]}
                          />
                          <div css={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <div>
                              <b>Contract ID:</b> #{offer.contract_id}
                            </div>
                            {/*{offer.offer_id && (*/}
                            {/*  <div>*/}
                            {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                            {/*  </div>*/}
                            {/*)}*/}
                            {/*<div>*/}
                            {/*  <b>Offer created:</b>{' '}*/}
                            {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                            {/*</div>*/}
                            <div>
                              <b>State: {text}</b>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                }>
                <div css={style.alignCenterWrapper} style={{ gap: '3px' }}>
                  {offers?.map((offer, index) => {
                    const { color, text } = getOfferStatusVisual(intl, offer);
                    return (
                      <Tooltip
                        key={index}
                        placement="top"
                        title={
                          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                            <div>
                              <b>Contract ID:</b> #{offer.contract_id}
                            </div>
                            {/*{offer.offer_id && (*/}
                            {/*  <div>*/}
                            {/*    <b>Offer ID:</b> #{offer.offer_id}*/}
                            {/*  </div>*/}
                            {/*)}*/}
                            {/*<div>*/}
                            {/*  <b>Offer created:</b>{' '}*/}
                            {/*  {offer.is_offer_created ? 'Yes' : 'No'}*/}
                            {/*</div>*/}
                            <div>
                              <b>State: {text}</b>
                            </div>
                          </div>
                        }>
                        <div
                          css={[style.tagPoint, style[color]]}
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    );
                  })}
                </div>
              </Popover>
            );
          }
        }
      },
    },
  ];

  const movementColumns = [
    {
      key: 'remove',
      width: 40,
      render: (_: any, record: TableEntry) => (
        <IconButton
          icon={['far', 'times']}
          color="red"
          click={() => {
            setSelectedItems?.((prevItems) =>
              prevItems.filter((item) => item.key !== record.key)
            );
          }}
          tooltip={intl.formatMessage({ id: 'remove-file' })}
        />
      ),
    },
    {
      key: 'icon',
      width: 40,
      render: () => (
        <IconButton
          icon={['far', 'paperclip']}
          backgroundOff
          customStyle={{ fontSize: 18 }}
        />
      ),
    },
    {
      title: intl.formatMessage({ id: 'actor' }),
      width: 300,
      render: (_: any, record: TableEntry) => (
        <div>
          <strong>{record?.actor}</strong>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'summary' }),
      render: (_: any, record: TableEntry) => <div>{record?.summary}</div>,
    },
    {
      title: intl.formatMessage({ id: 'createdAt' }),
      width: 300,
      render: (_: any, record: TableEntry) => (
        <div>{formatDate(record.createdAt, 'long')}</div>
      ),
    },
  ];

  const columnsToUse = useMemo(() => {
    if (isMovements) return movementColumns;
    if (isShares) return shareColumns;
    // Primary-/Secondary Share Keys: Contracts is meaningless for
    // secondary keys (no offers), same reasoning the main Subcontractors
    // table already applies via hideContractsColumn (useColumns.tsx).
    if (isOnSubcontractors) {
      return shareColumns.filter((c) => c.dataIndex !== 'contracts');
    }
    return columns;
  }, [isMovements, isShares, isOnSubcontractors]);

  /** Get DbFile[] from `selectedItems` */
  const getSelectedFiles = (
    customItems?: typeof selectedItems
  ): MergedDbFile[] => {
    const items = customItems || selectedItems;
    return (items
      .filter((item) => !item.folder)
      .map((item) => {
        // If we're on a tender page and the item has a dbFile, treat it as an attachment
        if (isOnTender && item.dbFile) {
          return item.dbFile;
        }
        return item.dbFile || item.dbAttachment;
      })
      .filter(Boolean) as unknown) as MergedDbFile[];
  };

  /** Get MovementEntry from `selectedItems` */
  const getSelectedMovements = (
    customItems?: typeof selectedItems
  ): MovementEntry[] => {
    const items = customItems || selectedItems;
    return items
      .filter((item) => item.actor && item.summary) // Only items that have movement properties
      .map((item) => ({
        id: item.id,
        originalId: typeof item.id === 'number' ? item.id : undefined,
        type: parseInt(item.description?.replace('Event type: ', '') || '0'),
        created_at: item.createdAt,
        created_by_user_id: 0, // Not available in TableEntry
        created_by_user_name: item.actor || '',
        target_entities: [
          {
            type: 'movement',
            display_name: item.name || '',
            description: item.description || null,
          },
        ],
        changes: [], // Not available in TableEntry
        custom: {
          note: item.summary || '',
          created_at: item.createdAt,
          created_by_user_id: 0, // Not available in TableEntry
          created_by_user_name: item.actor || '',
        },
        summary: {
          head: item.summary || '',
          actions: [],
        },
      }));
  };

  /** Get DbFolder[] from `selectedItems` */
  const getSelectedFolders = (customItems?: typeof selectedItems): DbFolder[] =>
    (customItems || selectedItems)
      .filter((item) => item.folder)
      .map((item) => item.dbFolder)
      .filter(Boolean) as DbFolder[];

  /** Get DbModuleFiles[] from `selectedItems` */
  const getSelectedModuleFiles = (
    customItems?: typeof selectedItems
  ): DbModuleFile[] =>
    (customItems || selectedItems)
      .filter((item) => item.moduleFile)
      .map((item) => item.dbModuleFile)
      .filter(Boolean) as DbModuleFile[];

  /** Get DbTenderFolder[] from `selectedItems` */
  const getSelectedTenderFolders = (
    customItems?: typeof selectedItems
  ): DbTenderFolder[] =>
    (customItems || selectedItems)
      .filter((item) => !!item.dbTenderFolder)
      .map((item) => item.dbTenderFolder)
      .filter(Boolean) as DbTenderFolder[];

  /** Get ClassicRegistration[] from selectedItems */
  const getSelectedClassicRegistrations = (
    customItems?: typeof selectedItems
  ): ClassicRegistration[] =>
    (customItems || selectedItems)
      .filter((item) => (item as any).classicRegistration)
      .map((item) => (item as any).classicRegistration)
      .filter(Boolean) as ClassicRegistration[];

  /** Get DbShare[] from selectedItems */
  const getSelectedShares = (customItems?: typeof selectedItems): DbShare[] => {
    const items = customItems || selectedItems;

    const shares = items
      .map((item) => {
        if (item.dbShare) return item.dbShare;

        const flat = item as Partial<DbShare>;

        if (flat.share_key_id && flat.share_id) {
          return {
            id: flat.id!,
            share_key_id: flat.share_key_id!,
            share_key_email: flat.share_key_email!,
            share_key_state: flat.share_key_state!,
            share_key_title: flat.share_key_title,
            share_key_user_id: flat.share_key_user_id,
            share_key_sector_id: flat.share_key_sector_id,
            share_key_expires_at: flat.share_key_expires_at!,
            share_key_is_claimed: flat.share_key_is_claimed!,
            share_key_opened_at: flat.share_key_opened_at!,
            share_key_claimed_at: flat.share_key_claimed_at!,
            share_key_first_invite_at: flat.share_key_first_invite_at!,
            share_id: flat.share_id!,
            share_active_from: flat.share_active_from!,
            share_expires_at: flat.share_expires_at!,
            share_title: flat.share_title,
            share_description: flat.share_description,
            share_state: flat.share_state!,
            module: flat.module || 0,
            project_id: flat.project_id || 0,
            registration_title: flat.registration_title,
            registration_description: flat.registration_description,
            project_data: flat.project_data!,
            offers: flat.offers || [],
          };
        }

        return null;
      })
      .filter(Boolean) as DbShare[];

    const unique = new Map<string, DbShare>();
    shares.forEach((share) => {
      if (!unique.has(share.share_key_id)) {
        unique.set(share.share_key_id, share);
      }
    });

    return Array.from(unique.values());
  };

  /**
   * !! Use lists returned here when triggering actions !!
   * Filters out folders’ descendants from actionable items,
   * otherwhise recursive actions might have unpredictable results
   */
  const getTreeUniqueItems = (): {
    files: MergedDbFile[];
    folders: DbFolder[];
    moduleFiles: DbModuleFile[];
    tenderFolders: DbTenderFolder[];
    shares: DbShare[];
    classicRegistrations: ClassicRegistration[];
  } => {
    // gather all folders
    const folders = getSelectedFolders();
    const tenderFolders = getSelectedTenderFolders();

    // if there are no folders, return files
    if (!folders.length && !tenderFolders.length) {
      return {
        files: getSelectedFiles(),
        folders: [],
        moduleFiles: getSelectedModuleFiles(),
        tenderFolders: [],
        shares: getSelectedShares(),
        classicRegistrations: getSelectedClassicRegistrations(),
      };
    }

    const badKeys: TableEntry['key'][] = []; // holds descendants' record keys

    folders.forEach((folder) => {
      const strFolderId = String(folder.id);

      // find folder descendants if any
      const descendants = selectedItems.filter(
        (item) => item.parentId === strFolderId
      );

      // add descendants' keys to the list
      badKeys.push(...descendants.map((d) => d.key));
    });

    tenderFolders.forEach((folder) => {
      const strFolderId = String(folder.id);

      // find folder descendants if any
      const descendants = selectedItems.filter(
        (item) => item.parentId === strFolderId
      );

      // add descendants' keys to the list
      badKeys.push(...descendants.map((d) => d.key));
    });

    // filter out items matching `badKeys`
    const filteredItems = selectedItems.filter(
      (item) => !badKeys.includes(item.key)
    );

    appLog('selectedItems raw:', selectedItems);
    appLog('extracted shares:', getSelectedShares(filteredItems));

    return {
      files: getSelectedFiles(filteredItems),
      folders: getSelectedFolders(filteredItems),
      moduleFiles: getSelectedModuleFiles(filteredItems),
      tenderFolders: getSelectedTenderFolders(filteredItems),
      shares: getSelectedShares(filteredItems),
      classicRegistrations: getSelectedClassicRegistrations(filteredItems),
    };
  };

  const { handleDownloadBasedOnPage } = useCherryPickFileDownload();
  const { changeDownloadingActive, changeDownloadingState } = useStore();

  // TODO: move download functionality in a hook

  const selectedFolders = selectedItems.filter((item) => item.folder === true)
    .length;

  const selectedFiles = selectedItems.filter((item) => item.dbFile).length;

  const selectedRegistrations = selectedItems.filter(
    (item) => (item as any).classicRegistration
  ).length;

  const selectedModuleFilesCount = selectedItems.filter(
    (item) => item.moduleFile
  ).length;

  const handleDownloadClick = async () => {
    changeDownloadingActive(true);
    changeDownloadingState(DownloadingState.PREPARING);
    const {
      files,
      folders,
      moduleFiles,
      tenderFolders,
      classicRegistrations,
    } = getTreeUniqueItems();
    return handleDownloadBasedOnPage(
      { files, folders, moduleFiles, tenderFolders, classicRegistrations },
      {
        allFolders: foldersQuery.data,
        onResetItemsList: () => setSelectedItems([]),
      }
    );
  };

  const handleRestoreClick = async () => {
    const commit = await doCreateCommit();
    doBulkRestore({
      projectId: Number(projectId),
      attachmentIds: selectedItems
        .filter(
          ({ state }) =>
            state === DB_STATE.REMOVED || state === DB_STATE.DELETED
        )
        .map((el) => el?.dbAttachment?.id) as string[],
      tenderId,
      // stateValue: DB_STATE.AVAILABLE,
      // commit,
    });
  };

  const doCreateCommit = async () => {
    if (!projectId) throw new Error('Missing project id.');

    const commitData = {
      description: `Restore files for project id = ${projectId}`,
      // TO DO: Check if important should be true/false
      is_important: true,
      state: DB_STATE.DRAFT,
    };

    return createCommit({
      projectId: String(projectId),
      commitData,
    }).catch((err) => {
      message.error(intl.formatMessage({ id: 'commit-create-failed' }));
      appLog('Restore files -> createCommit -> commit', commitData);
      appLog('Restore files -> createCommit -> err', err);
      throw err;
    });
  };

  /** lowest value from all items' parents permission levels  */
  const minPermissionLevel = useMemo(() => {
    return selectedItems.reduce((minVal: number, currentItem) => {
      let permission: number | undefined;

      // for root folders, use folder item permission
      if (currentItem.dbFolder?.parent_id === null) {
        permission = currentItem.dbFolder.permission;
      } else {
        const parentId = Number(currentItem.parentId);
        // find item's parent permission
        permission = foldersQuery.data?.find((folder) => folder.id === parentId)
          ?.permission;
      }

      // keep min value in accumulator
      return Math.min(minVal, permission || 99);
    }, RIGHTS_LEVEL.MANAGE);
  }, [selectedItems, foldersQuery.data]);

  const hasFolders = useMemo(() => selectedItems.some((item) => item.folder), [
    selectedItems,
  ]);

  const hasDuplicateNames = useMemo(
    () =>
      new Set(selectedItems.map((item) => item.name)).size !==
      selectedItems.length,
    [selectedItems]
  );

  const hasUnavailableFolders = useMemo(() => {
    let hasUnavailabe = false;
    selectedItems.forEach((item) => {
      const parentId = item.parentId === 'null' ? null : Number(item.parentId);
      const availableFolder =
        foldersQuery.data && isFolderAvailable(parentId, foldersQuery.data);
      if (!availableFolder) hasUnavailabe = true;
    });
    return hasUnavailabe;
  }, [selectedItems]);

  type GetButtonErrorsReturn = {
    [key in CherryBtnName]: string | undefined;
  };
  // TODO: refactor
  /**
   * Validate actions, setting error messages accordingly
   */

  const getButtonErrors = (): GetButtonErrorsReturn => {
    const buttonErrors: GetButtonErrorsReturn = {
      delete: undefined,
      move: undefined,
      copy: undefined,
      download: undefined,
      share: undefined,
      restore: undefined,
      resendInvite: undefined,
      addToVendors: undefined,
      correlationLetter: undefined,
      inviteVendorsModal: undefined,
    };

    // reuse permission error
    const permissionError = intl.formatMessage({ id: 'permission-restricted' });

    const hasTenderFolders = selectedItems.some(
      (item) => !!item.dbTenderFolder
    );

    // do checks per button, first permission, then go
    if (
      minPermissionLevel < RIGHTS_LEVEL.MANAGE &&
      window.sessionStorage.getItem('appState') !== 'superUser'
    ) {
      buttonErrors.delete = permissionError;
    }

    if (hasTenderFolders) {
      buttonErrors.delete = intl.formatMessage({
        id: 'delete-not-allowed-tender-folder',
      });
    }

    if (
      minPermissionLevel < RIGHTS_LEVEL.WRITE &&
      window.sessionStorage.getItem('appState') !== 'superUser'
    ) {
      buttonErrors.move = permissionError;
    } else if (hasDuplicateNames) {
      buttonErrors.move = intl.formatMessage({ id: 'no-duplicates' });
    }

    if (
      minPermissionLevel <= RIGHTS_LEVEL.OPEN &&
      window.sessionStorage.getItem('appState') !== 'superUser'
    ) {
      buttonErrors.copy = permissionError;
    } else if (hasDuplicateNames) {
      buttonErrors.copy = intl.formatMessage({ id: 'no-duplicates' });
    }

    if (
      minPermissionLevel < RIGHTS_LEVEL.OPEN &&
      window.sessionStorage.getItem('appState') !== 'superUser' &&
      !isOnModule
    ) {
      buttonErrors.download = permissionError;
    }

    if (!isSuperuser) {
      buttonErrors.share = permissionError;
    } else if (hasFolders) {
      buttonErrors.share = intl.formatMessage({ id: 'share-files-only' });
    }

    if (hasUnavailableFolders) {
      buttonErrors.restore = 'Not available';
    }

    return buttonErrors;
  };

  const showPdfCompareButton =
    selectedItems.length === 2 &&
    selectedItems.filter((item) => item?.type?.icon === 'file-pdf').length ===
      2;

  const panelHeader = (
    <Row align="middle">
      <IconButton
        icon={['far', 'times']}
        customStyle={style.icons}
        containerStyle={{ marginLeft: 'auto' }}
        tooltip={intl.formatMessage({ id: 'close' })}
        click={() => setSelectedItems([])}
      />

      <IconButton
        icon={['far', 'info-circle']}
        customStyle={style.icons}
        containerStyle={{ marginRight: 5 }}
        backgroundOff
        tooltip={intl.formatMessage({ id: 'cherrypick-info' })}
      />

      {intl.formatMessage({ id: 'details' })}
    </Row>
  );

  const hasRemoved = selectedItems.some(
    ({ state }) => state === DB_STATE.REMOVED || state === DB_STATE.DELETED
  );

  const buttonErrors = getButtonErrors();

  const selectedModulesFiles =
    selectedItems.filter((item) => item.moduleFile).length > 1;

  // Check if we have multiple registrations from different modules
  const selectedRegistrationItems = selectedItems.filter(
    (item) => (item as any).classicRegistration
  );

  const uniqueModules = new Set(
    selectedRegistrationItems.map(
      (item) => (item as any).classicRegistration?.module
    )
  );

  const hasMultipleModulesInRegistrations =
    selectedRegistrationItems.length > 1 && uniqueModules.size > 1;

  type ModuleFileDownload = {
    projectId: string;
    moduleFileId: string;
    moduleId: string | null;
    response_type: string;
    originalItem: TableEntry; // Full object for folder creation
  };

  const downloadModuleWithFolders = async () => {
    const selectedModuleFiles = selectedItems.filter(
      ({ moduleFile }) => moduleFile
    );

    setSelectedItems([]);

    if (selectedModuleFiles.length === 0) {
      return message.error(intl.formatMessage({ id: 'no-files-selected' }));
    }

    changeDownloadingActive(true);
    changeDownloadingState(DownloadingState.PREPARING);

    const processedModuleFiles: ModuleFileDownload[] = selectedModuleFiles
      .map(
        (file) =>
          file.dbModuleFile && {
            projectId: String(file.dbModuleFile.project_id),
            moduleFileId: String(file.dbModuleFile.id),
            moduleId: file.dbModuleFile.module
              ? String(file.dbModuleFile.module)
              : null,
            response_type: 'base64',
            originalItem: file, // Attach full object
          }
      )
      .filter(Boolean) as ModuleFileDownload[];

    if (processedModuleFiles.length === 0) {
      return message.error(
        intl.formatMessage({ id: 'invalid-files-selected' })
      );
    }

    try {
      await downloadMultipleModuleFilesWithFolders({
        moduleFiles: processedModuleFiles,
      });
    } catch (err) {
      appLog('downloadFile -> err', err);
      message.error(intl.formatMessage({ id: 'file-download-failed' }));
    }
  };

  const downloadRegistrationsWithFolders = async () => {
    const selectedRegistrations = selectedItems.filter(
      (item) => (item as any).classicRegistration
    );

    setSelectedItems([]);

    if (selectedRegistrations.length === 0) {
      return message.error(intl.formatMessage({ id: 'no-files-selected' }));
    }

    changeDownloadingActive(true);
    changeDownloadingState(DownloadingState.PREPARING);

    const classicRegs = selectedRegistrations
      .map((item) => (item as any).classicRegistration)
      .filter(Boolean);

    try {
      await downloadMultipleClassicRegistrationsWithFolders({
        classicRegistrations: classicRegs,
        onDownloadStarted: () => {
          // Message shown in download function
        },
      });
    } catch (err) {
      appLog('downloadRegistrationsWithFolders -> err', err);
      message.error(intl.formatMessage({ id: 'file-download-failed' }));
    }
  };

  const onComparePdfs = async () => {
    let url1 = '';
    let url2 = '';
    let name1 = '';
    let name2 = '';
    if (selectedItems[0].moduleFile) {
      const dbModuleFile = selectedItems[0].dbModuleFile as DbModuleFile;
      name1 = dbModuleFile.file_name;

      url1 = await getLinkModuleFile({
        projectId: String(dbModuleFile.project_id),
        moduleFileId: String(dbModuleFile.id),
        moduleId: dbModuleFile.module,
        response_type: 'base64',
      });
    } else if (selectedItems[0].dbFile) {
      const file = selectedItems[0]?.dbFile;
      name1 = file.title;
      const selectedIndex = fileVersionsContext[file.id]
        ? file.versions?.findIndex(
            (opt) => opt.id === fileVersionsContext[selectedItems[0].id]?.id
          )
        : 0;

      if ((selectedIndex as number) >= 0 && file.versions) {
        url1 = file?.versions[selectedIndex as number]?.location;
      }
      if (!url1) {
        const versions = await loadFileVersions('versions', {
          projectId: String(file.project_id),
          versionedFileId: file.id,
        });
        url1 = await requestFileLocation({
          fileId: versions[0].id,
          projectId: versions[0].project_id,
          versionedFileId: versions[0].versioned_file_id,
        });
      }
    }

    if (selectedItems[1].moduleFile) {
      const dbModuleFile = selectedItems[1].dbModuleFile as DbModuleFile;
      name2 = dbModuleFile.file_name;
      url2 = await getLinkModuleFile({
        projectId: String(dbModuleFile.project_id),
        moduleFileId: String(dbModuleFile.id),
        moduleId: dbModuleFile.module,
        response_type: 'base64',
      });
    } else if (selectedItems[1].dbFile) {
      const file = selectedItems[1]?.dbFile;
      name2 = file.title;
      const selectedIndex = fileVersionsContext[file.id]
        ? file.versions?.findIndex(
            (opt) => opt.id === fileVersionsContext[selectedItems[1].id]?.id
          )
        : 0;

      if ((selectedIndex as number) >= 0 && file.versions) {
        url2 = file?.versions[selectedIndex as number]?.location;
      }
      if (!url2) {
        const versions = await loadFileVersions('versions', {
          projectId: String(file.project_id),
          versionedFileId: file.id,
        });
        url2 = await requestFileLocation({
          fileId: versions[0].id,
          projectId: versions[0].project_id,
          versionedFileId: versions[0].versioned_file_id,
        });
      }
    }

    comparePdfModal.open({
      pdf1: url1 || '',
      pdf2: url2 || '',
      pdfName1: name1 || '',
      pdfName2: name2 || '',
    });
  };

  appLog(`CherryPick: selectedItems`, selectedItems);

  return (
    <div css={style.container}>
      <Collapse
        bordered={false}
        defaultActiveKey={['selectedItemsPanel']}
        css={style.properties}
        expandIconPosition="right"
        onChange={(...args) =>
          onCollapse && setTimeout(() => onCollapse(...args), 200)
        }>
        <Collapse.Panel header={panelHeader} key="selectedItemsPanel">
          <div css={style.pannelContainer}>
            <p css={{ position: 'absolute', left: 20 }}>
              {selectedFiles > 0 &&
                intl.formatMessage(
                  { id: 'selected-files' },
                  { numberOfFiles: selectedFiles }
                )}{' '}
              {selectedFolders > 0 &&
                intl.formatMessage(
                  { id: 'selected-folders' },
                  { numberOfFolders: selectedFolders }
                )}{' '}
              {selectedRegistrations > 0 &&
                intl.formatMessage(
                  { id: 'selected-registrations' },
                  { numberOfRegistrations: selectedRegistrations }
                )}{' '}
              {selectedModuleFilesCount > 0 &&
                intl.formatMessage(
                  { id: 'selected-module-files' },
                  { numberOfModuleFiles: selectedModuleFilesCount }
                )}
            </p>
            <div css={style.pannelTitle}>
              <IconButton
                icon={['far', 'file-alt']}
                customStyle={style.titleBtn}
                backgroundOff
              />
              {isMovements
                ? `${intl.formatMessage({ id: 'send-correction-letter' })} – ${
                    selectedItems.length
                  } ${intl.formatMessage({ id: 'changes' })}`
                : isOnSubcontractors
                ? `${intl.formatMessage({ id: 'tab-subcontractors' })} – ${
                    selectedItems.length
                  } ${intl.formatMessage({ id: 'selected' })}`
                : isShares
                ? `${intl.formatMessage({ id: 'shares' })} – ${
                    selectedItems.length
                  } ${intl.formatMessage({ id: 'selected' })}`
                : intl.formatMessage({ id: 'cherry-pick' })}
            </div>
            <Table
              css={{
                width: '100%',
                height: '100%',
              }}
              pagination={false}
              showHeader
              className={
                window.sessionStorage.getItem('appState') === 'timeMachine'
                  ? 'timeMachine-table'
                  : ''
              }
              rowClassName={
                window.sessionStorage.getItem('appState') === 'timeMachine'
                  ? 'timeMachine-row-table'
                  : ''
              }
              scroll={{ y: '21vh', x: 900 }}
              columns={columnsToUse}
              dataSource={selectedItems}
            />
            {window.sessionStorage.getItem('appState') === 'timeMachine' ? (
              <div style={{ display: 'flex' }}>
                <Button
                  type="primary"
                  onClick={handleDownloadClick}
                  css={[style.buttons, style.blue]}>
                  {intl.formatMessage({ id: 'download' })}
                </Button>
              </div>
            ) : window.sessionStorage.getItem('appState') === 'superUser' ? (
              <div style={{ display: 'flex' }}>
                {isOnDeleted && (
                  <Fragment>
                    <Button
                      type="primary"
                      title={buttonErrors.restore}
                      disabled={!!buttonErrors.restore}
                      onClick={handleRestoreClick}
                      css={[style.buttons, style.darkBlue]}>
                      {intl.formatMessage({ id: 'restore' })}
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        const { files } = getTreeUniqueItems();
                        restoreToModal.open({
                          files,
                          onDone: () => {
                            restoreToModal.dismiss();
                            setSelectedItems([]);
                          },
                        });
                      }}
                      css={[style.buttons, style.darkBlue]}>
                      {intl.formatMessage({ id: 'restore-to' })}
                    </Button>
                    <Button
                      type="primary"
                      css={[style.buttons, style.blue]}
                      disabled={!!buttonErrors.download}
                      title={buttonErrors.download}
                      onClick={handleDownloadClick}>
                      {intl.formatMessage({ id: 'download' })}
                    </Button>
                  </Fragment>
                )}
                {isOnArchive ||
                  (isOnSearch && (
                    <Fragment>
                      <Button
                        type="primary"
                        css={[style.buttons, style.red]}
                        disabled={!!buttonErrors.delete}
                        title={buttonErrors.delete}
                        onClick={() => {
                          const { files, folders } = getTreeUniqueItems();
                          // If we're on a tender page, pass files as attachments
                          if (isOnTender) {
                            deleteModal.open({
                              attachments: files,
                              folders,
                              onDone: () => {
                                deleteModal.dismiss();
                                setSelectedItems([]);
                              },
                            });
                          } else {
                            deleteModal.open({
                              files,
                              folders,
                              onDone: () => {
                                deleteModal.dismiss();
                                setSelectedItems([]);
                              },
                            });
                          }
                        }}>
                        {intl.formatMessage({ id: 'delete' })}
                      </Button>
                      <Button
                        type="primary"
                        css={[style.buttons, style.darkBlue]}
                        disabled={!!buttonErrors.move}
                        title={buttonErrors.move}
                        onClick={() => {
                          const { files, folders } = getTreeUniqueItems();
                          moveModal.open({
                            files,
                            folders,
                            folderId: currentFolder?.id || null,
                            onDone: () => {
                              moveModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        }}>
                        {intl.formatMessage({ id: 'move' })}
                      </Button>
                      <Button
                        type="primary"
                        css={[style.buttons, style.green]}
                        disabled={!!buttonErrors.copy}
                        title={buttonErrors.copy}
                        onClick={() => {
                          const { files, folders } = getTreeUniqueItems();
                          copyModal.open({
                            files,
                            folders,
                            folderId: currentFolder?.id || null,
                            onCancel: () => {
                              copyModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        }}>
                        {intl.formatMessage({ id: 'copy' })}
                      </Button>
                      <Button
                        type="primary"
                        css={[style.buttons, style.blue]}
                        disabled={!!buttonErrors.download}
                        title={buttonErrors.download}
                        onClick={handleDownloadClick}>
                        {intl.formatMessage({ id: 'download' })}
                      </Button>
                      <Button
                        type="primary"
                        css={[style.buttons, style.yellow]}
                        disabled={!!buttonErrors.share}
                        title={buttonErrors.share}
                        onClick={() =>
                          shareModal.open({
                            files: getSelectedFiles(),
                            onCancel: () => {
                              shareModal.dismiss();
                              setSelectedItems([]);
                            },
                          })
                        }>
                        {intl.formatMessage({ id: 'share' })}
                      </Button>
                    </Fragment>
                  ))}
              </div>
            ) : (
              <div style={{ display: 'flex' }}>
                {hasRemoved && !isShares && !isOnSubcontractors && (
                  <Button
                    type="primary"
                    title={buttonErrors.restore}
                    // disabled={!!buttonErrors.restore}
                    onClick={handleRestoreClick}
                    css={[style.buttons, style.darkBlue]}>
                    {intl.formatMessage({ id: 'restore' })}
                  </Button>
                )}

                {isShares && (
                  <div>
                    <Button
                      type="primary"
                      css={[style.buttons, style.green]}
                      disabled={!!buttonErrors.inviteVendorsModal}
                      title={buttonErrors.inviteVendorsModal}
                      onClick={() =>
                        inviteVendorsModal.open({
                          initialShares: selectedItems,
                          mode: 'edit-resend',
                          onCancel: () => {
                            inviteVendorsModal.dismiss();
                            setSelectedItems([]);
                          },
                        })
                      }>
                      {intl.formatMessage({ id: 'offer-resend-invite' })}
                    </Button>

                    {/*<Button*/}
                    {/*  type="primary"*/}
                    {/*  css={[style.buttons, style.blue]}*/}
                    {/*  disabled={!!buttonErrors.addToVendors}*/}
                    {/*  title={buttonErrors.addToVendors}*/}
                    {/*  onClick={() => {*/}
                    {/*    message.error(`Not implemented`);*/}
                    {/*  }}>*/}
                    {/*  {intl.formatMessage({ id: 'add-to-vendors' })}*/}
                    {/*</Button>*/}

                    {hasRemovedShares && (
                      <Button
                        type="primary"
                        css={[style.buttons]}
                        onClick={() => {
                          const { shares } = getTreeUniqueItems();
                          reactivateShareModal.open({
                            share: shares,
                            onCancel: () => {
                              reactivateShareModal.dismiss();
                              setSelectedItems([]);
                            },
                            onDone: () => {
                              reactivateShareModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        }}>
                        {intl.formatMessage({
                          id: 'reactivate',
                        })}
                      </Button>
                    )}
                  </div>
                )}

                {/* Primary-/Secondary Share Keys (vendor tab): Subcontractors
                    bulk bar - Resend-Invite/Reactivate/Delete, same button
                    set/labels/colors as admin's own Vendors bulk bar above,
                    but disabled on the tender-closed rule instead of admin's
                    folder-permission checks (no vendor-side equivalent). */}
                {isOnSubcontractors && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {hasNonRemovedSubcontractorShares && (
                      <Button
                        type="primary"
                        css={[style.buttons, style.green]}
                        disabled={subcontractorsTenderCompleted}
                        title={
                          subcontractorsTenderCompleted
                            ? intl.formatMessage({
                                id: 'tender-data-completed',
                              })
                            : undefined
                        }
                        onClick={() =>
                          setSubcontractorsBulkAction({
                            type: 'resend-invite',
                            shares: getTreeUniqueItems().shares.filter(
                              (s) => s.share_key_state !== DB_STATE.REMOVED
                            ),
                          })
                        }>
                        {intl.formatMessage({ id: 'resend-invite' })}
                      </Button>
                    )}
                    {hasRemovedSubcontractorShares && (
                      <Button
                        type="primary"
                        css={[style.buttons]}
                        disabled={subcontractorsTenderCompleted}
                        title={
                          subcontractorsTenderCompleted
                            ? intl.formatMessage({
                                id: 'tender-data-completed',
                              })
                            : undefined
                        }
                        onClick={() =>
                          setSubcontractorsBulkAction({
                            type: 'reactivate',
                            shares: getTreeUniqueItems().shares.filter(
                              (s) => s.share_key_state === DB_STATE.REMOVED
                            ),
                          })
                        }>
                        {intl.formatMessage({ id: 'reactivate' })}
                      </Button>
                    )}
                    {hasNonRemovedSubcontractorShares && (
                      <Button
                        type="primary"
                        css={[style.buttons, style.red]}
                        disabled={subcontractorsTenderCompleted}
                        title={
                          subcontractorsTenderCompleted
                            ? intl.formatMessage({
                                id: 'tender-data-completed',
                              })
                            : undefined
                        }
                        onClick={() =>
                          setSubcontractorsBulkAction({
                            type: 'remove',
                            shares: getTreeUniqueItems().shares.filter(
                              (s) => s.share_key_state !== DB_STATE.REMOVED
                            ),
                          })
                        }>
                        {intl.formatMessage({ id: 'delete' })}
                      </Button>
                    )}
                  </div>
                )}

                {/* --- Movements Actions (Project Side) --- */}
                {isMovements && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {(() => {
                      const selectedMovements = getSelectedMovements();
                      const ids = selectedMovements
                        .map((m) => Number(m.id))
                        .filter((id) => !isNaN(id));

                      const canAdd = selectedItems.some(
                        (item) =>
                          item.dbTenderMovement?.report_data
                            ?.is_in_current_report === false
                      );

                      const canRemove = selectedItems.some(
                        (item) =>
                          item.dbTenderMovement?.report_data
                            ?.is_in_current_report === true
                      );

                      const canIgnore = selectedItems.some(
                        (item) =>
                          item.dbTenderMovement?.report_data?.is_ignore ===
                          false
                      );

                      const canReactivate = selectedItems.some(
                        (item) =>
                          item.dbTenderMovement?.report_data?.is_ignore === true
                      );

                      return (
                        <Fragment>
                          {canAdd && (
                            <Button
                              type="primary"
                              css={[style.buttons, style.blue]}
                              onClick={() =>
                                createMutationTrigger('add_movement', ids)
                              }>
                              {intl.formatMessage({
                                id: 'add-to-correction-letter',
                              })}
                            </Button>
                          )}

                          {canRemove && (
                            <Button
                              css={[style.buttons]}
                              onClick={() =>
                                createMutationTrigger('remove_movement', ids)
                              }>
                              {intl.formatMessage({
                                id: 'remove-from-correction-letter',
                              })}
                            </Button>
                          )}

                          {canIgnore && (
                            <Button
                              type="danger"
                              css={[style.buttons, style.red]}
                              onClick={() => {
                                ids.forEach((id) =>
                                  updateFlagsMutation(id, 'set_ignore_on')
                                );
                                setSelectedItems([]);
                              }}>
                              {intl.formatMessage({ id: 'ignore' })}
                            </Button>
                          )}

                          {canReactivate && (
                            <Button
                              css={[style.buttons, style.green]}
                              style={{ color: 'white' }}
                              onClick={() => {
                                ids.forEach((id) =>
                                  updateFlagsMutation(id, 'set_ignore_off')
                                );
                                setSelectedItems([]);
                              }}>
                              {intl.formatMessage({ id: 'reactivate' })}
                            </Button>
                          )}
                        </Fragment>
                      );
                    })()}
                  </div>
                )}

                {!isOnModule &&
                  !isMovements &&
                  !isOnMyTender &&
                  !isOnRegistrations && (
                    <Button
                      type="primary"
                      css={[style.buttons, style.red]}
                      disabled={!!buttonErrors.delete}
                      title={buttonErrors.delete}
                      onClick={() => {
                        const { files, folders, shares } = getTreeUniqueItems();

                        if (isShares) {
                          appLog(
                            '33. isShares. Selected items:',
                            selectedItems
                          );
                          // console.log('33. isShares. Extracted shares:', shares);
                          deleteModal.open({
                            shares,
                            onDone: () => {
                              deleteModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        } else if (isOnTender) {
                          deleteModal.open({
                            attachments: files,
                            folders,
                            onDone: () => {
                              deleteModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        } else {
                          deleteModal.open({
                            files,
                            folders,
                            onDone: () => {
                              deleteModal.dismiss();
                              setSelectedItems([]);
                            },
                          });
                        }
                      }}>
                      {intl.formatMessage({ id: 'delete' })}
                    </Button>
                  )}

                {isOnArchive && !isOnRegistrations && (
                  <Fragment>
                    <Button
                      type="primary"
                      css={[style.buttons, style.darkBlue]}
                      disabled={!!buttonErrors.move}
                      title={buttonErrors.move}
                      onClick={() => {
                        const { files, folders } = getTreeUniqueItems();
                        moveModal.open({
                          files,
                          folders,
                          folderId: currentFolder?.id || null,
                          onDone: () => {
                            moveModal.dismiss();
                            setSelectedItems([]);
                          },
                        });
                      }}>
                      {intl.formatMessage({ id: 'move' })}
                    </Button>
                    <Button
                      type="primary"
                      css={[style.buttons, style.green]}
                      disabled={!!buttonErrors.copy}
                      title={buttonErrors.copy}
                      onClick={() => {
                        const { files, folders } = getTreeUniqueItems();
                        copyModal.open({
                          files,
                          folders,
                          folderId: currentFolder?.id || null,
                          onCancel: () => {
                            copyModal.dismiss();
                            setSelectedItems([]);
                          },
                        });
                      }}>
                      {intl.formatMessage({ id: 'copy' })}
                    </Button>
                  </Fragment>
                )}

                {!isShares && !isMovements && !isOnSubcontractors && (
                  <Button
                    type="primary"
                    css={[style.buttons, style.blue]}
                    disabled={!!buttonErrors.download}
                    title={buttonErrors.download}
                    onClick={handleDownloadClick}>
                    {intl.formatMessage({ id: 'download' })}
                  </Button>
                )}

                {/* only show Share for SU */}
                {/* {isSuperuser && !isOnModule && (
                  <Button
                    type="primary"
                    css={[style.buttons, style.yellow]}
                    disabled={!!buttonErrors.share}
                    title={buttonErrors.share}
                    onClick={() =>
                      shareModal.open({
                        files: getSelectedFiles(),
                        onDone: () => {
                          shareModal.dismiss();
                          setSelectedItems([]);
                        },
                      })
                    }>
                    {intl.formatMessage({ id: 'share' })}
                  </Button>
                )} */}
                {showPdfCompareButton && (
                  <Button
                    type="primary"
                    css={[style.buttons, style.green]}
                    title="Compare"
                    onClick={onComparePdfs}>
                    {intl.formatMessage({ id: 'compare' })}
                  </Button>
                )}
                {selectedModulesFiles && (
                  <Button
                    type="primary"
                    css={[style.buttons, style.cyan]}
                    title="Download with Folders"
                    onClick={downloadModuleWithFolders}>
                    {/*{intl.formatMessage({ id: 'download-with-folder' })}*/}
                    Download with Folder
                  </Button>
                )}
                {hasMultipleModulesInRegistrations && (
                  <Button
                    type="primary"
                    css={[style.buttons, style.cyan]}
                    title="Download with Folders (by Module)"
                    onClick={downloadRegistrationsWithFolders}>
                    Download with Folder
                  </Button>
                )}
              </div>
            )}
          </div>
        </Collapse.Panel>
      </Collapse>

      {/* Primary-/Secondary Share Keys (vendor tab): Subcontractors bulk
          action confirm modals, driven by subcontractorsBulkAction. */}
      <SimpleDeleteModal
        modalTitle={
          (subcontractorsBulkAction?.shares.length || 0) <= 1
            ? `${intl.formatMessage({ id: 'share-delete' })} "${
                subcontractorsBulkAction?.shares[0]?.share_key_title
              }"`
            : intl.formatMessage(
                { id: 'secondary-share-key-delete-confirm-multiple' },
                { count: subcontractorsBulkAction?.shares.length || 0 }
              )
        }
        modalIcon={['far', 'trash']}
        okText={intl.formatMessage({ id: 'delete' })}
        cancelText={intl.formatMessage({ id: 'cancel' })}
        customText={intl.formatMessage({ id: 'delete-share-warning' })}
        isLoading={isBulkChangingSecondaryShareKeyState}
        onOk={() =>
          subcontractorsBulkAction &&
          doBulkChangeSecondaryShareKeyState({
            shares: subcontractorsBulkAction.shares,
            action: 'remove',
          })
        }
        onCancel={() => setSubcontractorsBulkAction(null)}
        visible={subcontractorsBulkAction?.type === 'remove'}
      />
      <SimpleConfirmModal
        modalTitle={
          subcontractorsBulkAction?.type === 'resend-invite'
            ? (subcontractorsBulkAction?.shares.length || 0) <= 1
              ? intl.formatMessage(
                  { id: 'secondary-share-key-resend-invite-confirm-single' },
                  { name: subcontractorsBulkAction?.shares[0]?.share_key_title }
                )
              : intl.formatMessage(
                  {
                    id: 'secondary-share-key-resend-invite-confirm-multiple',
                  },
                  { count: subcontractorsBulkAction?.shares.length || 0 }
                )
            : (subcontractorsBulkAction?.shares.length || 0) <= 1
            ? intl.formatMessage(
                { id: 'reactivate-share-confirm' },
                { name: subcontractorsBulkAction?.shares[0]?.share_key_title }
              )
            : intl.formatMessage(
                { id: 'reactivate-share-confirm-multiple' },
                { count: subcontractorsBulkAction?.shares.length || 0 }
              )
        }
        modalIcon={
          subcontractorsBulkAction?.type === 'resend-invite'
            ? ['far', 'envelope']
            : ['far', 'repeat']
        }
        okText={
          subcontractorsBulkAction?.type === 'resend-invite'
            ? intl.formatMessage({ id: 'resend-invite' })
            : intl.formatMessage({ id: 'reactivate' })
        }
        cancelText={intl.formatMessage({ id: 'cancel' })}
        isLoading={
          subcontractorsBulkAction?.type === 'resend-invite'
            ? isBulkResendingSecondaryInvite
            : isBulkChangingSecondaryShareKeyState
        }
        onOk={() => {
          if (!subcontractorsBulkAction) return;
          if (subcontractorsBulkAction.type === 'resend-invite') {
            doBulkResendSecondaryInvite({
              shares: subcontractorsBulkAction.shares,
            });
          } else {
            doBulkChangeSecondaryShareKeyState({
              shares: subcontractorsBulkAction.shares,
              action: 'reactivate',
            });
          }
        }}
        onCancel={() => setSubcontractorsBulkAction(null)}
        visible={
          subcontractorsBulkAction?.type === 'reactivate' ||
          subcontractorsBulkAction?.type === 'resend-invite'
        }
      />
    </div>
  );
};

export default CherryPickPanel;

type CherryBtnName =
  | 'delete'
  | 'move'
  | 'copy'
  | 'download'
  | 'share'
  | 'restore'
  | 'resendInvite'
  | 'addToVendors'
  | 'correlationLetter'
  | 'inviteVendorsModal';
